import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { AlertCircle, Calculator, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const SCORING_METHODS = [
  { value: 'yes_no', label: 'Yes/No Points', description: 'Auto-score true / false style responses.' },
  { value: 'count', label: 'Count Items', description: 'Auto-score based on number of items entered.' },
  { value: 'manual', label: 'Manual Score', description: 'Reviewer enters a score manually.' },
  { value: 'numeric', label: 'Numeric Value', description: 'Auto-score from the numeric response.' },
];

const getSuggestedMethod = (fieldType) => {
  if (['checkbox', 'radio'].includes(fieldType)) return 'yes_no';
  if (['array', 'repeatable_group', 'checkbox_group'].includes(fieldType)) return 'count';
  if (fieldType === 'number') return 'numeric';
  return 'manual';
};

const getRuleMaxScore = (rule = {}) => {
  const method = rule?.scoring_method || 'manual';

  if (method === 'yes_no') {
    return Math.max(Number(rule?.yes_value ?? 0), Number(rule?.no_value ?? 0));
  }

  return Number(rule?.max_score ?? 0);
};

const normalizeRule = (rule, fieldType = 'text') => {
  const normalizedRule = {
    field_id: rule?.field_id || '',
    scoring_method: rule?.scoring_method || getSuggestedMethod(fieldType),
    max_score: Number(rule?.max_score ?? 5),
    yes_value: Number(rule?.yes_value ?? 1),
    no_value: Number(rule?.no_value ?? 0),
    per_item_score: Number(rule?.per_item_score ?? 1),
    description: rule?.description || '',
  };

  return {
    ...normalizedRule,
    max_score: getRuleMaxScore(normalizedRule),
  };
};

const buildTemplateFromFormConfig = (formConfig = [], existingTemplate = { sections: [] }) => {
  const existingSections = new Map((existingTemplate?.sections || []).map((section) => [section.section_id, section]));

  return {
    sections: formConfig
      .filter((section) => section.enabled !== false)
      .map((section) => {
        const currentSection = existingSections.get(section.id);
        const validFieldIds = new Set((section.fields || []).map((field) => field.id));
        const fieldRules = (currentSection?.field_rules || [])
          .filter((rule) => validFieldIds.has(rule.field_id))
          .map((rule) => {
            const matchedField = section.fields.find((field) => field.id === rule.field_id);
            return normalizeRule(rule, matchedField?.type);
          });

        return {
          section_id: section.id,
          section_title: section.title,
          weight: Number(currentSection?.weight ?? 1),
          max_score: fieldRules.reduce((sum, rule) => sum + getRuleMaxScore(rule), 0),
          field_rules: fieldRules,
        };
      }),
  };
};

const createDefaultRule = (field) => normalizeRule({ field_id: field.id }, field.type);

const normalizeTemplate = (template, formConfig = []) => ({
  sections: (template?.sections || [])
    .filter((section) => formConfig.some((formSection) => formSection.id === section.section_id && formSection.enabled !== false))
    .map((section) => ({
      ...section,
      max_score: section.field_rules.reduce((sum, rule) => sum + getRuleMaxScore(rule), 0),
      field_rules: section.field_rules.map((rule) =>
        normalizeRule(
          rule,
          formConfig
            .find((formSection) => formSection.id === section.section_id)
            ?.fields?.find((field) => field.id === rule.field_id)?.type
        )
      ),
    })),
});

const ScoringTemplateEditor = ({ formConfig, scoringTemplate, onChange = null }) => {
  const [template, setTemplate] = useState({ sections: [] });
  const [expandedSections, setExpandedSections] = useState([]);
  const templateRef = useRef(template);
  const pendingParentSyncRef = useRef(false);

  useEffect(() => {
    templateRef.current = template;
  }, [template]);

  useEffect(() => {
    const nextTemplate = buildTemplateFromFormConfig(formConfig, scoringTemplate);
    if (JSON.stringify(nextTemplate) !== JSON.stringify(templateRef.current)) {
      setTemplate(nextTemplate);
      setExpandedSections(nextTemplate.sections.map((section) => section.section_id));
    }
  }, [formConfig, scoringTemplate]);

  const enabledSections = useMemo(
    () => (formConfig || []).filter((section) => section.enabled !== false),
    [formConfig]
  );

  const getSectionConfig = (sectionId) => template.sections.find((section) => section.section_id === sectionId);

  const getFieldById = (sectionId, fieldId) => {
    const section = enabledSections.find((item) => item.id === sectionId);
    return section?.fields?.find((field) => field.id === fieldId) || null;
  };

  const getAvailableFields = (sectionId, currentFieldId = null) => {
    const formSection = enabledSections.find((section) => section.id === sectionId);
    const sectionConfig = getSectionConfig(sectionId);
    const usedFieldIds = new Set(
      (sectionConfig?.field_rules || [])
        .filter((rule) => rule.field_id !== currentFieldId)
        .map((rule) => rule.field_id)
    );

    return (formSection?.fields || []).filter((field) => !usedFieldIds.has(field.id));
  };

  useEffect(() => {
    if (pendingParentSyncRef.current && onChange) {
      pendingParentSyncRef.current = false;
      onChange(normalizeTemplate(template, enabledSections));
    }
  }, [template, enabledSections, onChange]);

  const updateTemplateSection = (sectionIndex, updater) => {
    pendingParentSyncRef.current = true;
    setTemplate((currentTemplate) => {
      const nextSections = [...currentTemplate.sections];
      nextSections[sectionIndex] = updater(nextSections[sectionIndex]);
      return { ...currentTemplate, sections: nextSections };
    });
  };

  const addFieldRule = (sectionIndex) => {
    const section = template.sections[sectionIndex];
    const availableFields = getAvailableFields(section.section_id);

    if (availableFields.length === 0) {
      toast.error('All fields in this section already have scoring rules.');
      return;
    }

    updateTemplateSection(sectionIndex, (currentSection) => ({
      ...currentSection,
      field_rules: [...currentSection.field_rules, createDefaultRule(availableFields[0])],
    }));
  };

  const updateFieldRule = (sectionIndex, ruleIndex, field, value) => {
    updateTemplateSection(sectionIndex, (currentSection) => {
      const nextRules = [...currentSection.field_rules];
      const currentRule = nextRules[ruleIndex];
      nextRules[ruleIndex] = normalizeRule({ ...currentRule, [field]: value }, getFieldById(currentSection.section_id, currentRule.field_id)?.type);
      return { ...currentSection, field_rules: nextRules };
    });
  };

  const updateRuleField = (sectionIndex, ruleIndex, fieldId) => {
    const section = template.sections[sectionIndex];
    const field = getFieldById(section.section_id, fieldId);
    const suggestedMethod = getSuggestedMethod(field?.type);

    updateTemplateSection(sectionIndex, (currentSection) => {
      const nextRules = [...currentSection.field_rules];
      nextRules[ruleIndex] = {
        ...normalizeRule(nextRules[ruleIndex], field?.type),
        field_id: fieldId,
        scoring_method: suggestedMethod,
      };
      nextRules[ruleIndex] = normalizeRule(nextRules[ruleIndex], field?.type);
      return { ...currentSection, field_rules: nextRules };
    });
  };

  const removeFieldRule = (sectionIndex, ruleIndex) => {
    updateTemplateSection(sectionIndex, (currentSection) => ({
      ...currentSection,
      field_rules: currentSection.field_rules.filter((_, index) => index !== ruleIndex),
    }));
  };

  const calculateSectionMax = (section) => section.field_rules.reduce((sum, rule) => sum + getRuleMaxScore(rule), 0);
  const totalPossibleScore = template.sections.reduce((sum, section) => sum + calculateSectionMax(section), 0);

  if (!enabledSections.length) {
    return (
      <div className="py-10 text-center text-slate-500" data-testid="scoring-template-empty-state">
        <AlertCircle className="mx-auto mb-4 h-12 w-12 opacity-50" />
        <p>Please configure the application form before setting up scoring.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="scoring-template-editor">
      <div className="flex flex-col gap-4 rounded-sm border border-slate-200 bg-white p-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h3 className="font-heading text-lg font-semibold text-slate-900">Scoring Template</h3>
          <p className="text-sm text-slate-500" data-testid="scoring-template-total-possible-score">
            Configure field-by-field scoring rules. Total possible score: <strong>{totalPossibleScore}</strong>
          </p>
          <p className="mt-2 text-xs text-slate-500" data-testid="scoring-template-save-path-note">
            Changes here are saved when you use the scholarship dialog&apos;s Save or Save &amp; Close buttons.
          </p>
        </div>
      </div>

      <Accordion type="multiple" value={expandedSections} onValueChange={setExpandedSections}>
        {template.sections.map((section, sectionIndex) => {
          const sectionMax = calculateSectionMax(section);
          const availableFields = getAvailableFields(section.section_id);

          return (
            <AccordionItem
              key={section.section_id}
              value={section.section_id}
              className="mb-3 rounded-sm border bg-white"
              data-testid={`scoring-section-${section.section_id}`}
            >
              <AccordionTrigger className="px-4 py-3 hover:no-underline">
                <div className="flex w-full items-center justify-between gap-4 pr-4 text-left">
                  <div className="flex items-center gap-3">
                    <Calculator className="h-5 w-5 text-greek-blue" />
                    <div>
                      <p className="font-medium text-slate-900">{section.section_title}</p>
                      <p className="text-xs text-slate-500">
                        {section.field_rules.length} rule{section.field_rules.length === 1 ? '' : 's'} configured
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-greek-blue">{sectionMax} pts</p>
                    <p className="text-xs text-slate-500">section max</p>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4">
                <div className="space-y-4">
                  {section.field_rules.length === 0 ? (
                    <div className="rounded-sm border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-center">
                      <p className="mb-3 text-sm text-slate-500">No scoring rules configured for this section yet.</p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => addFieldRule(sectionIndex)}
                        className="rounded-sm"
                        data-testid={`add-scoring-rule-button-${section.section_id}`}
                      >
                        <Plus className="mr-1 h-4 w-4" />
                        Add First Rule
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {section.field_rules.map((rule, ruleIndex) => {
                        const field = getFieldById(section.section_id, rule.field_id);
                        const availableRuleFields = getAvailableFields(section.section_id, rule.field_id);

                        return (
                          <div
                            key={`${section.section_id}-${rule.field_id}-${ruleIndex}`}
                            className="rounded-sm border border-slate-200 bg-slate-50 p-4"
                            data-testid={`scoring-rule-${section.section_id}-${ruleIndex}`}
                          >
                            <div className="grid gap-3 lg:grid-cols-[2fr,1.4fr,repeat(2,minmax(0,1fr)),auto]">
                              <div>
                                <Label className="text-xs text-slate-600">Field to score</Label>
                                <Select value={rule.field_id} onValueChange={(value) => updateRuleField(sectionIndex, ruleIndex, value)}>
                                  <SelectTrigger
                                    className="mt-1 rounded-sm"
                                    data-testid={`field-rule-field-select-${section.section_id}-${ruleIndex}`}
                                  >
                                    <SelectValue placeholder="Select a field" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {availableRuleFields.map((item) => (
                                      <SelectItem key={item.id} value={item.id}>
                                        {item.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>

                              <div>
                                <Label className="text-xs text-slate-600">Scoring method</Label>
                                <Select
                                  value={rule.scoring_method}
                                  onValueChange={(value) => updateFieldRule(sectionIndex, ruleIndex, 'scoring_method', value)}
                                >
                                  <SelectTrigger
                                    className="mt-1 rounded-sm"
                                    data-testid={`field-rule-method-select-${section.section_id}-${ruleIndex}`}
                                  >
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {SCORING_METHODS.map((method) => (
                                      <SelectItem key={method.value} value={method.value}>
                                        {method.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <p className="mt-1 text-xs text-slate-500">{SCORING_METHODS.find((method) => method.value === rule.scoring_method)?.description}</p>
                              </div>

                              {rule.scoring_method === 'yes_no' ? (
                                <>
                                  <div>
                                    <Label className="text-xs text-slate-600">Yes points</Label>
                                    <Input
                                      type="number"
                                      min="0"
                                      value={rule.yes_value}
                                      onChange={(event) => updateFieldRule(sectionIndex, ruleIndex, 'yes_value', Number(event.target.value || 0))}
                                      className="mt-1 rounded-sm"
                                      data-testid={`field-rule-yes-value-${section.section_id}-${ruleIndex}`}
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-xs text-slate-600">No points</Label>
                                    <Input
                                      type="number"
                                      min="0"
                                      value={rule.no_value}
                                      onChange={(event) => updateFieldRule(sectionIndex, ruleIndex, 'no_value', Number(event.target.value || 0))}
                                      className="mt-1 rounded-sm"
                                      data-testid={`field-rule-no-value-${section.section_id}-${ruleIndex}`}
                                    />
                                  </div>
                                </>
                              ) : (
                                <>
                                  <div>
                                    <Label className="text-xs text-slate-600">{rule.scoring_method === 'count' ? 'Points / item' : 'Max score'}</Label>
                                    <Input
                                      type="number"
                                      min="1"
                                      value={rule.scoring_method === 'count' ? rule.per_item_score : rule.max_score}
                                      onChange={(event) => updateFieldRule(
                                        sectionIndex,
                                        ruleIndex,
                                        rule.scoring_method === 'count' ? 'per_item_score' : 'max_score',
                                        Number(event.target.value || 1)
                                      )}
                                      className="mt-1 rounded-sm"
                                      data-testid={`field-rule-primary-score-input-${section.section_id}-${ruleIndex}`}
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-xs text-slate-600">Max score</Label>
                                    <Input
                                      type="number"
                                      min="1"
                                      value={rule.max_score}
                                      onChange={(event) => updateFieldRule(sectionIndex, ruleIndex, 'max_score', Number(event.target.value || 1))}
                                      className="mt-1 rounded-sm"
                                      data-testid={`field-rule-max-score-${section.section_id}-${ruleIndex}`}
                                    />
                                  </div>
                                </>
                              )}

                              <div className="flex items-end justify-end">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeFieldRule(sectionIndex, ruleIndex)}
                                  className="text-red-600 hover:bg-red-50 hover:text-red-700"
                                  data-testid={`remove-scoring-rule-button-${section.section_id}-${ruleIndex}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>

                            <div className="mt-3 grid gap-3 md:grid-cols-[1fr,auto] md:items-end">
                              <div>
                                <Label className="text-xs text-slate-600">Reviewer guidance</Label>
                                <Input
                                  value={rule.description}
                                  onChange={(event) => updateFieldRule(sectionIndex, ruleIndex, 'description', event.target.value)}
                                  placeholder="Add instructions reviewers should follow for this field."
                                  className="mt-1 rounded-sm"
                                  data-testid={`field-rule-guidance-input-${section.section_id}-${ruleIndex}`}
                                />
                              </div>
                              <div className="rounded-sm border border-slate-200 bg-white px-3 py-2 text-sm text-slate-500" data-testid={`field-rule-summary-${section.section_id}-${ruleIndex}`}>
                                <span className="font-medium text-slate-700">{field?.label || 'Field not found'}</span>
                                <span className="ml-2">• {field?.type || 'unknown'} field</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {availableFields.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => addFieldRule(sectionIndex)}
                      className="rounded-sm"
                      data-testid={`append-scoring-rule-button-${section.section_id}`}
                    >
                      <Plus className="mr-1 h-4 w-4" />
                      Add Scoring Rule
                    </Button>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>

      <div className="rounded-sm border border-greek-blue/20 bg-greek-blue/5 p-4" data-testid="scoring-template-summary-card">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h4 className="font-medium text-slate-900">Scoring Summary</h4>
            <p className="text-sm text-slate-600">
              {template.sections.filter((section) => section.field_rules.length > 0).length} of {template.sections.length} sections configured
            </p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-greek-blue">{totalPossibleScore}</p>
            <p className="text-sm text-slate-500">Total max score</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScoringTemplateEditor;
